/**
 * monthlyColumns (#2684 Part B) — the row model behind the ``Säulen``
 * presentation of the Liquiditätskurve: one Einnahmen/Ausgaben pair per
 * calendar month.
 *
 * **Why it aggregates ``LedgerRow[]`` and not the daily balance series.** The
 * daily series is a *balance* (a stock): each point is the account level after
 * that day, so recovering the month's gross in- and outflows from it is only
 * possible as a NET (``balance[end] − balance[start]``) — which would draw one
 * 10.000 € sliver for a month that moved 800.000 € in and 790.000 € out. The
 * ledger rows are the *flows* themselves, and they are the exact rows the
 * Buchungen list renders below the chart. Aggregating them is therefore what
 * makes a bar and the list underneath it agree by construction rather than by
 * coincidence — that reconciliation is this module's acceptance criterion, and
 * it is asserted directly against ``rowsInMonth`` in the tests.
 */
import { chartWindowMonths, windowFirstYm, windowLastYm } from './sharedChartWindow';
import type { LedgerCategory } from './ledgerCategory';
import type { LedgerRow } from './buildLedgerRows';

/** ``YYYY-MM``. Anything else is import junk and buckets into no month. */
const YM_RE = /^\d{4}-\d{2}$/;

/**
 * The one category whose share each bar reports separately (#2743): movement
 * between the tenant's OWN accounts. Deliberately a single id, not the
 * ``CROSSCUTTING_CASH`` set — see the accumulator's comment.
 */
const TRANSFER_CATEGORY: LedgerCategory = 'transfer_internal';

/** How the balance chart is drawn. Orthogonal to the ``?ym=``/``?wj=`` period. */
export type ChartPresentation = 'kurve' | 'saeulen';

export const DEFAULT_PRESENTATION: ChartPresentation = 'kurve';

/**
 * Resolve the ``?pm=`` URL param. Binary with a stable default (unlike
 * Ernteprognose's tri-state, whose default is derived from ``?m=``), so the
 * page mirrors Preisspiegel: an unknown or absent value degrades to the curve
 * and the default is kept implicit in the URL.
 */
export function resolvePresentation(param: string | null): ChartPresentation {
  return param === 'saeulen' ? 'saeulen' : DEFAULT_PRESENTATION;
}

export interface MonthlyColumn {
  /** ``YYYY-MM``. */
  ym: string;
  /** Gross inflow, ``≥ 0``. */
  einnahmenEur: number;
  /**
   * Gross outflow, ``≤ 0`` — the ledger's own sign convention
   * (``LedgerRow.amountEur``: income +, expense −) is carried through rather
   * than flipped to a magnitude, so the reconciliation with ``rowsInMonth`` is
   * a plain sum with no ``Math.abs`` in the middle of it. The chart draws the
   * pair diverging around a zero baseline.
   */
  ausgabenEur: number;
  /**
   * The ``transfer_internal`` portion of ``einnahmenEur`` (``≥ 0``) and of
   * ``ausgabenEur`` (``≤ 0``) — #2743.
   *
   * Own money moved between the farmer's OWN accounts is real bank movement, so
   * it stays in the gross above (dropping it would break the hero ↔ bars ↔ curve
   * ↔ Buchungen tie that ``CROSSCUTTING_CASH`` exists to hold under the #2664
   * single-account filter). But on WF it is **30,6 % of every Einnahmen bar and
   * 28,5 % of every Ausgaben bar**, peaking at 68,4 % — so a bar that says
   * nothing about it reads as revenue that never happened. Reported here so the
   * chart can draw it as a muted sub-segment WITHIN the unchanged bar.
   */
  einnahmenTransferEur: number;
  ausgabenTransferEur: number;
  /**
   * The rest of the gross, i.e. ``einnahmenEur − einnahmenTransferEur`` (resp.
   * the Ausgaben pair) — the operative part, in the series colour.
   *
   * Since #2955 §3 this is a ROLL-UP rather than a drawn segment: the chart
   * draws the ist/plan pair that partitions it (below), and this is the figure
   * that pair is derived from and sums back to.
   *
   * Precomputed rather than subtracted at render time for two reasons: the
   * difference of two cent-rounded figures still carries float residue
   * (``0.3 − 0.1 === 0.19999999999999998``), which would leave a hairline gap
   * between the stacked segments; and it keeps ``ohneTransfer + transfer ===
   * gross`` a property this module can assert, rather than one the chart has to
   * be trusted to preserve.
   */
  einnahmenOhneTransferEur: number;
  ausgabenOhneTransferEur: number;
  /**
   * The three Ausgaben figures above as POSITIVE MAGNITUDES (#2955 §2) — the
   * two-way roll-ups the four DRAWN entries partition.
   *
   * They were "the numbers the chart draws, and nothing else" until #2955 §3
   * split each side again by ``isActual``; the chart now reads the four
   * ist/plan magnitudes below, and these are what those sum back to. Kept
   * rather than retired because they are the shape the reconciliation
   * invariant is stated in, and because the geometry test's unsplit baseline
   * render measures against them.
   *
   * Since #2955 the two sides rise from a SHARED zero baseline instead of
   * diverging around one, so the Ausgaben bar needs a length rather than a
   * signed offset. These are DERIVED and ADDITIVE on purpose: the signed fields
   * above stay the source of truth, so the ``rowsInMonth`` reconciliation — this
   * module's acceptance criterion — remains a plain sum with no ``Math.abs`` in
   * the middle of it, and the tooltip can keep printing Ausgaben negative (see
   * ``ColumnTooltip``'s sign-convention note) while the axis is a magnitude
   * scale. Flipping the originals instead would have bought the same bars and
   * lost all three properties.
   *
   * There is deliberately NO Einnahmen mirror: those are already ``≥ 0``, and a
   * second name for the same number is a thing to keep in sync for nothing.
   */
  ausgabenBetragEur: number;
  ausgabenTransferBetragEur: number;
  ausgabenOhneTransferBetragEur: number;
  /**
   * THE FOUR ENTRIES EACH SIDE IS DRAWN AS (#2955 §3), baseline outward:
   * ``ist-operativ`` · ``plan-operativ`` · ``ist-transfer`` · ``plan-transfer``.
   *
   * They refine the two-way #2743 split by ``LedgerRow.isActual``, and that
   * refinement is the whole point: **"geplant" is a property of a TRANSACTION,
   * not of a month.** ``isForecast`` below grades whole months and therefore
   * cannot express the month the Ist horizon falls INSIDE — which holds booked
   * rows up to the horizon and planned ones after it, and which used to render
   * wholly solid while the page's own caption said „Ist bis … · Prognose ab …".
   *
   * Each side's four values are a PARTITION of that side's gross, so the drawn
   * stack's total height is unchanged and the bar still reconciles against the
   * Buchungen list. The partition is exact by construction rather than by
   * rounding luck: the ``ist`` halves are rounded from their own accumulators
   * and the ``plan`` halves are then taken as the COMPLEMENT of the already-
   * rounded two-way figures above, so ``ist + plan`` is that figure to the cent
   * (a float sum of two cent values can still carry sub-cent residue — see the
   * ``toBeCloseTo`` in the tests; it is sub-pixel in the plot).
   *
   * The Ausgaben four are MAGNITUDES only, with no signed twin. The split
   * exists solely to be DRAWN — the tooltip and the ``rowsInMonth``
   * reconciliation both read the signed totals above — so a signed mirror would
   * be eight more names to keep in sync for nothing, which is the same call the
   * Einnahmen side's missing magnitude mirror makes in the other direction.
   */
  einnahmenIstOhneTransferEur: number;
  einnahmenPlanOhneTransferEur: number;
  einnahmenIstTransferEur: number;
  einnahmenPlanTransferEur: number;
  ausgabenIstOhneTransferBetragEur: number;
  ausgabenPlanOhneTransferBetragEur: number;
  ausgabenIstTransferBetragEur: number;
  ausgabenPlanTransferBetragEur: number;
  /**
   * True once the month lies strictly PAST the Ist horizon's month, i.e. it is
   * pure projection. The horizon's own month counts as Ist: it carries real
   * booked data, it is the month the "Ist bis 30.06." caption names, and it is
   * what ``latestIstMonth`` selects as the newest Ist month — so grading it as
   * Prognose would contradict three surfaces at once. An EMPTY horizon (the
   * neutral/empty series) makes every month a forecast, matching
   * ``LedgerRow.isActual``, which is false for every row in that case.
   *
   * SCOPE, since #2955 §3: this MONTH-grain flag drives the Ist↔Prognose
   * boundary marker (via ``forecastBoundaryYm``) and nothing else. It used to
   * drive the forecast hatch too, all-or-nothing per month; the row-grain split
   * above subsumes that and is strictly finer, so leaving both grains on the
   * hatch is the drift this scope note exists to prevent. The two are not in
   * conflict where they disagree: the marker fences off the first month that is
   * ENTIRELY projection, while the hatch marks the individual planned rows,
   * which begin partway through the horizon's own month — so a little hatch
   * legitimately appears to the LEFT of the marker.
   */
  isForecast: boolean;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * The drawn magnitude of a signed outflow (#2955 §2).
 *
 * The zero case is the whole reason this is a named function rather than a
 * unary minus at the three call sites: ``-0`` is what ``-(0)`` evaluates to,
 * ``Object.is(-0, 0)`` is false, and the fixed ±12-month window emits a zero
 * column for every month the ledger does not reach — which for the average
 * tenant is most of the 25. A ``-0`` there is invisible in the plot and loud in
 * every ``toBe(0)`` / ``toEqual`` assertion downstream.
 */
const magnitude = (signed: number): number => (signed === 0 ? 0 : -signed);

/**
 * The geplant half of a two-way figure, as the COMPLEMENT of its ist half
 * (#2955 §3) — ``planIn`` for the ``≥ 0`` Einnahmen side, ``planOut`` for the
 * Ausgaben side, which returns the drawn MAGNITUDE of a ``≤ 0`` figure.
 *
 * Taking the complement rather than accumulating a second sum is what makes
 * ``ist + plan`` equal the figure it splits to the cent, so hatching part of a
 * bar cannot change the bar's height.
 *
 * CLAMPED, and not defensively-for-its-own-sake. Both inputs are cent-rounded,
 * so the complement is normally exact — but a row whose amount carries SUB-cent
 * precision (a planned ``total_eur`` derived from qty × unit price does) can
 * round the whole up and its ist part down, and the complement then lands one
 * cent on the wrong side of zero. The clamp costs nothing in the ordinary case
 * and rules out two bad ends in the pathological one: a negative stack entry,
 * which d3's ``stackOffsetNone`` draws as an inverted sliver at the top of the
 * stack (a bar reading one cent too tall), and a ``-0``, which is invisible in
 * the plot and loud in every ``toBe(0)`` downstream — the same trap
 * ``magnitude`` above exists for.
 */
const planIn = (whole: number, ist: number): number => Math.max(0, round2(whole - ist));
const planOut = (whole: number, ist: number): number => magnitude(Math.min(0, round2(whole - ist)));

/**
 * Aggregate ledger rows into one column pair per month, ascending by ``ym``,
 * over the FIXED ``[todayYm−12mo … todayYm+12mo]`` window (#2955 §1). Rows are
 * bucketed by their own ``date``, exactly like ``rowsInMonth``.
 *
 * **Empty months are emitted as zero columns**, all the way to the window
 * edges. The chart's x-axis is CATEGORICAL, so a skipped month is not a gap —
 * it closes up, and a July bar next to a January one renders as though they
 * were consecutive. The curve solves it the same way (``fixedChartWindow``
 * null-pads every empty day).
 *
 * The span used to be the ledger's OWN first…last month, which is why switching
 * presentation silently changed the time range: on WF the columns began seven
 * months before the curve's window and stopped fourteen months before its end,
 * and the two modes' zoom/pan could not be shared because they were not looking
 * at the same timeline. A row outside the window is now simply dropped rather
 * than stretching the axis to reach it — which also retires the old
 * ``MAX_FILLED_MONTHS`` guard, since there is no unbounded span left to guard.
 *
 * The knock-on the issue calls out and accepts: a tenant with no planned rows
 * renders empty future columns. That is honest — it is the same thing the
 * curve's flat projection says — and it is what gives the Prognose hatch
 * anything to mark at all.
 */
export function monthlyColumns(
  rows: readonly LedgerRow[],
  istHorizon: string,
  todayYm: string,
): MonthlyColumn[] {
  const boundary = istHorizon.slice(0, 7); // 'YYYY-MM-DD' → 'YYYY-MM'; '' when neutral
  const firstYm = windowFirstYm(todayYm);
  const lastYm = windowLastYm(todayYm);
  const byYm = new Map<
    string,
    {
      einnahmenEur: number;
      ausgabenEur: number;
      einnahmenTransferEur: number;
      ausgabenTransferEur: number;
      // #2955 §3 — only the IST halves are accumulated; the geplant halves are
      // taken as the complement below, which is what makes ``ist + plan`` equal
      // the two-way figure exactly instead of to within a rounding step. Rows
      // are added to BOTH a gross accumulator above and its ist sub-accumulator
      // here rather than the gross being re-derived from the parts, so the
      // existing sums — and with them the reconciliation AC — are byte-identical.
      einnahmenIstOhneTransferEur: number;
      einnahmenIstTransferEur: number;
      ausgabenIstOhneTransferEur: number;
      ausgabenIstTransferEur: number;
    }
  >();

  for (const r of rows) {
    // A zero-balance-effect row (a planned row inside the confirmed-actual
    // window that the bank never confirmed, OR — #3065 — a settled contracted-
    // only collective-credit expectation whose money is already counted via its
    // `payed` sibling elsewhere) has ZERO effect on the balance. Drawing it would
    // be a phantom bar that the curve, the Kontostand column and the Saldo all
    // disagree with. Deliberately ``zeroBalanceEffect``, NOT ``didNotHappen`` —
    // the settled shape renders un-greyed in the ledger but must still exclude
    // here, same reasoning as ``buildLedgerRows.ts``'s ``withRunningBalance``.
    if (r.zeroBalanceEffect) continue;
    const ym = r.date.slice(0, 7);
    // Defensive: a row with a missing or malformed date belongs to no month.
    // A bare '' or a '2026'/'not-a-date' would otherwise sort into the series
    // and reach the axis formatter as an "Invalid Date" tick.
    if (!YM_RE.test(ym)) continue;
    // Outside the fixed window the row has no column to land in. Dropping it
    // here rather than after aggregation keeps the map bounded by the window
    // instead of by whatever a mistyped ``value_date`` invents.
    if (ym < firstYm || ym > lastYm) continue;
    let bucket = byYm.get(ym);
    if (!bucket) {
      bucket = {
        einnahmenEur: 0,
        ausgabenEur: 0,
        einnahmenTransferEur: 0,
        ausgabenTransferEur: 0,
        einnahmenIstOhneTransferEur: 0,
        einnahmenIstTransferEur: 0,
        ausgabenIstOhneTransferEur: 0,
        ausgabenIstTransferEur: 0,
      };
      byYm.set(ym, bucket);
    }
    // #2743 — ONLY ``transfer_internal``. ``vat`` and ``unsorted`` are its
    // fellow ``CROSSCUTTING_CASH`` members, but USt cash really leaves the farm
    // and ``unsorted`` is merely not-yet-classified: muting either would be a
    // lie. A row with no category (a legacy bank fact) is not a transfer.
    const isTransfer = r.ledgerCategory === TRANSFER_CATEGORY;
    // #2955 §3 — the row grain. ``isActual`` is the ROW's own answer to "has the
    // bank confirmed this yet" (``date <= istHorizon``), which is why it can
    // split a month that ``isForecast`` grades wholly Ist.
    const isIst = r.isActual;
    if (r.amountEur > 0) {
      bucket.einnahmenEur += r.amountEur;
      if (isTransfer) bucket.einnahmenTransferEur += r.amountEur;
      if (isIst) {
        if (isTransfer) bucket.einnahmenIstTransferEur += r.amountEur;
        else bucket.einnahmenIstOhneTransferEur += r.amountEur;
      }
    } else if (r.amountEur < 0) {
      bucket.ausgabenEur += r.amountEur;
      if (isTransfer) bucket.ausgabenTransferEur += r.amountEur;
      if (isIst) {
        if (isTransfer) bucket.ausgabenIstTransferEur += r.amountEur;
        else bucket.ausgabenIstOhneTransferEur += r.amountEur;
      }
    }
  }

  // Round only at the end: per-row rounding would drift a long month.
  const column = (ym: string): MonthlyColumn => {
    const b = byYm.get(ym);
    const einnahmenEur = b ? round2(b.einnahmenEur) : 0;
    const ausgabenEur = b ? round2(b.ausgabenEur) : 0;
    const einnahmenTransferEur = b ? round2(b.einnahmenTransferEur) : 0;
    const ausgabenTransferEur = b ? round2(b.ausgabenTransferEur) : 0;
    // Re-round the difference of two already-rounded figures — see the field's
    // doc comment: the subtraction itself reintroduces float residue.
    const ausgabenOhneTransferEur = round2(ausgabenEur - ausgabenTransferEur);
    const einnahmenOhneTransferEur = round2(einnahmenEur - einnahmenTransferEur);
    // #2955 §3 — the ist halves come from their own accumulators; the geplant
    // halves are the COMPLEMENT of the rounded two-way figures, never a second
    // accumulator. That is what makes ``ist + plan`` equal the figure it splits
    // to the cent, so hatching a part of the bar cannot change the bar's height.
    const einnahmenIstOhneTransferEur = b ? round2(b.einnahmenIstOhneTransferEur) : 0;
    const einnahmenIstTransferEur = b ? round2(b.einnahmenIstTransferEur) : 0;
    const ausgabenIstOhneTransferEur = b ? round2(b.ausgabenIstOhneTransferEur) : 0;
    const ausgabenIstTransferEur = b ? round2(b.ausgabenIstTransferEur) : 0;
    return {
      ym,
      einnahmenEur,
      ausgabenEur,
      einnahmenTransferEur,
      ausgabenTransferEur,
      einnahmenOhneTransferEur,
      ausgabenOhneTransferEur,
      // #2955 §2 — mirrored from the ROUNDED signed values, so the drawn stack
      // sums to the drawn gross exactly rather than to within a rounding step.
      ausgabenBetragEur: magnitude(ausgabenEur),
      ausgabenTransferBetragEur: magnitude(ausgabenTransferEur),
      ausgabenOhneTransferBetragEur: magnitude(ausgabenOhneTransferEur),
      einnahmenIstOhneTransferEur,
      einnahmenPlanOhneTransferEur: planIn(einnahmenOhneTransferEur, einnahmenIstOhneTransferEur),
      einnahmenIstTransferEur,
      einnahmenPlanTransferEur: planIn(einnahmenTransferEur, einnahmenIstTransferEur),
      ausgabenIstOhneTransferBetragEur: magnitude(ausgabenIstOhneTransferEur),
      ausgabenPlanOhneTransferBetragEur: planOut(
        ausgabenOhneTransferEur,
        ausgabenIstOhneTransferEur,
      ),
      ausgabenIstTransferBetragEur: magnitude(ausgabenIstTransferEur),
      ausgabenPlanTransferBetragEur: planOut(ausgabenTransferEur, ausgabenIstTransferEur),
      isForecast: ym > boundary,
    };
  };

  return chartWindowMonths(todayYm).map(column);
}

/**
 * Whether the columns have anything to draw — the analogue of the curve's
 * ``hasData``, and the reason the chart's empty state survived #2955 §1.
 *
 * The array is now always 25 long, so its LENGTH can no longer answer "is there
 * a booking to show": an empty ledger and a full one both yield 25 columns.
 * Evaluated over the fixed window, exactly like the curve's own check, so a
 * tenant whose only rows sit outside ±12 months gets the same honest "nothing
 * in this window" answer in both presentations.
 */
export function hasColumnData(columns: readonly MonthlyColumn[]): boolean {
  return columns.some((c) => c.einnahmenEur !== 0 || c.ausgabenEur !== 0);
}

/**
 * #3158 — "does ``ym`` lie fully after the Ist horizon?": the ``isForecast``
 * PREDICATE (see that field's doc block: the horizon's OWN month counts as Ist,
 * so a mid-month horizon keeps its month mixed) as a shared pure helper, so
 * CategoryFeed's post-horizon gating and the column grading cannot drift apart.
 * Reusing the ``MonthlyColumn.isForecast`` FIELD instead is exactly the
 * scope-widening its doc forbids.
 *
 * One deliberate divergence: an EMPTY horizon ('' — the neutral/empty series)
 * returns FALSE here, where the field grades every month forecast. The field
 * marks a boundary (no horizon ⇒ everything is projection); this helper HIDES
 * Ist-bisher surfaces, and a fact-less tenant must not have them blanket-hidden
 * (the #2269 ``todayYm === undefined ⇒ never suppress`` precedent).
 */
export function isFullyPostHorizon(ym: string, istHorizon: string): boolean {
  return istHorizon !== '' && ym > istHorizon.slice(0, 7);
}

/**
 * The first Prognose month that directly follows an Ist one — i.e. the month
 * whose LEFT edge is the Ist↔Prognose boundary (#2955 §4).
 *
 * ``null`` when the given slice holds no such step: an empty Ist horizon grades
 * every month as projection, a horizon past the window grades none, and a
 * zoomed or panned slice can sit entirely on one side. All three must draw NO
 * marker rather than clamp one to an edge — a "Heute" rule at the extreme left
 * of the plot would assert a boundary exactly where there is not one.
 *
 * Returns the month AFTER the step, not the last Ist month, because the chart
 * draws the marker with ``position="start"``: recharts places a categorical
 * ``ReferenceLine`` at the band's CENTRE by default, which would strike the
 * first Prognose pair through the middle instead of fencing it off.
 *
 * Takes the columns rather than the horizon date so it reads whatever slice the
 * chart is showing, and so the "which month counts as Ist" rule (the horizon's
 * OWN month does — see ``isForecast``) is stated in exactly one place.
 */
export function forecastBoundaryYm(columns: readonly MonthlyColumn[]): string | null {
  for (let i = 1; i < columns.length; i += 1) {
    if (columns[i].isForecast && !columns[i - 1].isForecast) return columns[i].ym;
  }
  return null;
}
